import { useParams } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";
import { PriorityPartner } from "@/components/PriorityPartner";
import { FAQ, buildFAQSchema } from "@/components/FAQ";
import { findGuide, guides } from "@/data/guides";
import { Link } from "react-router-dom";
import { guidePath } from "@/lib/format";
import { NotFoundPage } from "./NotFoundPage";
import { useMemo } from "react";

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^\p{Letter}\p{Number}]+/gu, "-")
    .replace(/^-|-$/g, "");
}

export function GuidePage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const guide = findGuide(slug);
  if (!guide) return <NotFoundPage />;

  const title = isSv ? guide.title_sv : guide.title_en;
  const intro = isSv ? guide.intro_sv : guide.intro_en;
  const excerpt = isSv ? guide.excerpt_sv : guide.excerpt_en;
  const takeaways = isSv ? guide.takeaways_sv : guide.takeaways_en;

  const sections = useMemo(
    () =>
      guide.sections.map((s) => ({
        ...s,
        id: slugify(isSv ? s.heading_sv : s.heading_en),
      })),
    [guide.sections, isSv],
  );

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    datePublished: guide.updated_at,
    dateModified: guide.updated_at,
    author: { "@type": "Organization", name: "CasinoMenu.se" },
    publisher: {
      "@type": "Organization",
      name: "CasinoMenu.se",
      logo: { "@type": "ImageObject", url: "https://casinomenu.se/favicon.svg" },
    },
    description: excerpt,
    inLanguage: isSv ? "sv-SE" : "en-GB",
  };

  const relatedGuides = guides
    .filter(
      (g) =>
        g.id !== guide.id &&
        (g.category === guide.category || g.category === "responsible"),
    )
    .slice(0, 4);

  return (
    <div className="container-page py-8">
      <SEO
        title={title}
        description={excerpt}
        type="article"
        jsonLd={guide.faq.length ? [articleSchema, buildFAQSchema(guide.faq, lang)] : [articleSchema]}
      />
      <Breadcrumbs
        items={[
          { label: t("nav.home"), href: prefix },
          {
            label: isSv ? "Guider" : "Guides",
            href: `${prefix}/${isSv ? "guider" : "guides"}`,
          },
          { label: title },
        ]}
      />

      <div className="mt-3 grid gap-8 lg:grid-cols-[1fr_320px]">
        <article className="min-w-0">
          <header>
            <span className="eyebrow">
              {labelForCategory(guide.category, isSv)}
            </span>
            <h1 className="mt-3 text-3xl font-extrabold leading-tight md:text-5xl">
              {title}
            </h1>
            <p className="mt-3 max-w-2xl text-lg text-ink-600">{excerpt}</p>
            <p className="mt-3 text-xs text-ink-500">
              {t("label.lastUpdated")}: {isSv ? "22 oktober 2026" : "22 October 2026"} ·{" "}
              {isSv ? "Av CasinoMenu.se redaktion" : "By the CasinoMenu.se editors"}
            </p>
          </header>

          {intro && (
            <p className="mt-6 text-lg leading-relaxed text-ink-800">{intro}</p>
          )}

          {sections.length > 1 && (
            <nav
              aria-label={isSv ? "Innehåll" : "Contents"}
              className="mt-8 rounded-2xl border border-ink-100 bg-ink-50 p-5"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                {isSv ? "Innehåll" : "Contents"}
              </p>
              <ol className="mt-3 grid gap-1.5 text-sm md:grid-cols-2">
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="flex items-start gap-2 text-ink-800 no-underline hover:text-brand-700"
                    >
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded bg-white text-[11px] font-bold text-brand-700">
                        {i + 1}
                      </span>
                      <span>{isSv ? s.heading_sv : s.heading_en}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          <div className="mt-10 space-y-10">
            {sections.map((s) => {
              const paragraphs = isSv ? s.paragraphs_sv : s.paragraphs_en;
              const list = isSv ? s.list_sv : s.list_en;
              return (
                <section
                  key={s.id}
                  id={s.id}
                  className="scroll-mt-24"
                >
                  <h2 className="text-2xl font-bold text-ink-900 md:text-3xl">
                    {isSv ? s.heading_sv : s.heading_en}
                  </h2>
                  <div className="mt-3 space-y-4 text-[16px] leading-relaxed text-ink-800">
                    {paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                  {list && list.length > 0 && (
                    <ul className="mt-4 space-y-2 rounded-xl border border-ink-100 bg-white p-5 text-sm text-ink-800">
                      {list.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span aria-hidden className="mt-0.5 text-brand-500">
                            ●
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>

          {takeaways.length > 0 && (
            <section className="mt-12 rounded-2xl border border-gold-300/60 bg-gradient-to-br from-gold-200/40 to-white p-6">
              <h2 className="text-xl font-bold text-ink-900">
                {isSv ? "Snabbsammanfattning" : "Key takeaways"}
              </h2>
              <ul className="mt-3 grid gap-2 text-sm md:grid-cols-2">
                {takeaways.map((tk) => (
                  <li key={tk} className="flex items-start gap-2 text-ink-800">
                    <span aria-hidden className="mt-0.5 text-amber-600">
                      ★
                    </span>
                    <span>{tk}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {guide.faq.length > 0 && (
            <div className="mt-12">
              <FAQ items={guide.faq} />
            </div>
          )}

          {relatedGuides.length > 0 && (
            <section className="mt-12">
              <h2 className="text-xl font-bold">
                {isSv ? "Relaterade guider" : "Related guides"}
              </h2>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {relatedGuides.map((g) => (
                  <Link
                    key={g.id}
                    to={guidePath(prefix, lang, isSv ? g.slug_sv : g.slug_en)}
                    className="card-interactive p-4 no-underline"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-brand-700">
                      {labelForCategory(g.category, isSv)}
                    </p>
                    <p className="mt-1 font-semibold text-ink-900">
                      {isSv ? g.title_sv : g.title_en}
                    </p>
                    <p className="mt-1 text-sm text-ink-600 line-clamp-2">
                      {isSv ? g.excerpt_sv : g.excerpt_en}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <PriorityPartner
            variant="side"
            placement="guide-side"
            source={`guide-${slug}`}
          />
        </aside>
      </div>

      <InternalLinks context="guide" />
    </div>
  );
}

function labelForCategory(cat: string, isSv: boolean) {
  if (isSv) {
    if (cat === "license") return "Licens";
    if (cat === "responsible") return "Ansvar";
    if (cat === "rules") return "Regler";
    if (cat === "terms") return "Termer";
    if (cat === "esports") return "Esport";
    if (cat === "betting") return "Betting";
    if (cat === "payment") return "Betalning";
    return cat;
  }
  if (cat === "license") return "License";
  if (cat === "responsible") return "Safety";
  if (cat === "rules") return "Rules";
  if (cat === "terms") return "Terms";
  if (cat === "esports") return "Esports";
  if (cat === "betting") return "Betting";
  if (cat === "payment") return "Payment";
  return cat;
}
